import { watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useAuthStore } from '@shared/stores/auth'
import { handleParameter } from '@/helpers/QueryParameterHelper'

const APPCUES_FLOW_QUERY_KEY = 'appcues_flow'

interface AppcuesApi {
  show: (flowId: string) => unknown
}

function getAppcues(): AppcuesApi | undefined {
  return (window as unknown as { Appcues?: AppcuesApi }).Appcues
}

function flowIdFromLocation(): string | null {
  const fromSearch = new URLSearchParams(window.location.search).get(APPCUES_FLOW_QUERY_KEY)
  if (fromSearch) {
    return fromSearch
  }
  const hash = window.location.hash
  const queryStart = hash.indexOf('?')
  if (queryStart === -1) {
    return null
  }
  return new URLSearchParams(hash.slice(queryStart + 1)).get(APPCUES_FLOW_QUERY_KEY)
}

export function useAppcuesFlow(): void {
  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()

  const shownFlowIds = new Set<string>()

  let pendingFlowId: string | null = flowIdFromLocation()

  function refreshPendingFlowId(): void {
    if (pendingFlowId) {
      return
    }
    pendingFlowId = handleParameter(route.query[APPCUES_FLOW_QUERY_KEY]) ?? flowIdFromLocation()
  }

  function tryShowFlow(): void {
    refreshPendingFlowId()

    const appcues = getAppcues()
    if (!appcues || !authStore.userId || !pendingFlowId) {
      return
    }

    const flowId = pendingFlowId
    if (shownFlowIds.has(flowId)) {
      return
    }
    shownFlowIds.add(flowId)

    try {
      const result = appcues.show(flowId) as Promise<unknown> | undefined
      if (result && typeof result.then === 'function') {
        result.catch(() => {
          shownFlowIds.delete(flowId)
        })
      }
    } catch {
      shownFlowIds.delete(flowId)
    }
  }

  function scheduleShowAfterRender(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(tryShowFlow)
    })
  }

  router.isReady().then(scheduleShowAfterRender)

  router.afterEach(() => {
    scheduleShowAfterRender()
  })

  window.addEventListener('appcues:ready', tryShowFlow)

  watch(
    () => authStore.userId,
    userId => {
      if (userId) {
        scheduleShowAfterRender()
      }
    }
  )
}

export default useAppcuesFlow
